import { UserInfo } from '@thedealersconcierge/lib/auth';
import store from '~/lib/store';
import { ActiveDealership, dealershipAtom } from '~/state';

/**
 * The cache principal for dealership-scoped data: a dealership plus the viewing
 * staff member's permission identity.
 *
 * The viewer is part of the principal because responses are viewer-shaped: the
 * transaction selector gates fields with `@include(permissionChecker(...))`,
 * and the transaction list is role-scoped server-side. `UserInfo`
 * (`{ role, extraPermissions }`) is exactly the permission identity that drives
 * those differences — and it is what the active-dealership atom already carries,
 * so no userId plumbing is needed and permission changes refresh the cache.
 */
export type DealershipScope = {
  dealershipId: string;
  viewer: UserInfo;
};

export type ChatScope = {
  chatId: string;
};

/**
 * Tier prefixes, defined once so every entity shares the exact same root. That
 * shared root is what makes "purge a whole dealership on switch" a single
 * `removeQueries({ queryKey: ['ds', dealershipId] })`.
 */
export const scopeKey = {
  ds: (s: DealershipScope) =>
    [
      'ds',
      s.dealershipId,
      // extraPermissions is sorted so the key is order-independent: React Query's
      // hashKey sorts object keys but not array elements, and the server returns
      // this array in an unspecified order.
      {
        role: s.viewer.role,
        extraPermissions: [...s.viewer.extraPermissions].sort()
      }
    ] as const,
  user: (viewerId: string) => ['user', viewerId] as const,
  chat: (chatScope: ChatScope) => ['chat', chatScope.chatId] as const,
  global: () => ['global'] as const
};

/** Build a scope from the active-dealership perms object. */
export const dealershipScope = (adp: ActiveDealership): DealershipScope => ({
  dealershipId: adp.dealershipId,
  viewer: { role: adp.role, extraPermissions: adp.extraPermissions }
});

/**
 * Read the current scope from the active-dealership atom — the same source the
 * GraphQL client uses for the `DealershipId` header. Returns `undefined` when no
 * dealership is active (before sign-in / dealership selection).
 */
export const getActiveDealershipScope = (): DealershipScope | undefined => {
  const adp = store.instance.get(dealershipAtom)?.activeDealershipPerms;
  return adp ? dealershipScope(adp) : undefined;
};

/**
 * Sentinel scope for keying a query constructed before a dealership is active.
 * It is fed through the same key factory so the key keeps its canonical shape
 * (never a hand-rolled literal). The non-UUID `dealershipId` guarantees it can
 * never collide with a real dealership's keys, and the entry is replaced once a
 * real scope arrives; the `role` is arbitrary and matches nothing.
 */
export const NO_ACTIVE_DEALERSHIP_SCOPE: DealershipScope = {
  dealershipId: 'no-active-dealership',
  viewer: { role: 'SALES_PERSON', extraPermissions: [] }
};
